import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Info, ChevronDown, ChevronUp } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API_BASE = process.env.REACT_APP_BACKEND_URL;
let CACHE_KEY = 'merke_ui_collapsed';
let prefsPromise = null;
let prefsUser = null;
const loadServerPrefs = (userId) => {
  if (prefsUser !== userId) { prefsPromise = null; prefsUser = userId; }
  if (!prefsPromise) {
    const token = localStorage.getItem('token');
    prefsPromise = axios.get(`${API_BASE}/api/merkeskyting/my-preferences`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.data?.ui_collapsed || {})
      .catch(() => { prefsPromise = null; return null; });
  }
  return prefsPromise;
};

const readCache = () => {
  try { return JSON.parse(localStorage.getItem(CACHE_KEY) || '{}'); } catch { return {}; }
};

const VARIANTS = {
  blue: 'bg-blue-500/10 border-blue-500/30 text-blue-200',
  emerald: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-200',
  sky: 'bg-sky-500/10 border-sky-500/30 text-sky-200',
};
const isMobile = () => typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches;

export const CollapsibleInfoTip = ({ prefKey, title, children, variant = 'blue', icon: Icon = Info, mobileCollapsed = false }) => {
  const { user } = useAuth();
  CACHE_KEY = `merke_ui_collapsed:${user?.id || 'anon'}`;
  const [mobileMode] = useState(() => mobileCollapsed && isMobile());
  const [collapsed, setCollapsed] = useState(() => mobileMode || !!readCache()[prefKey]);

  useEffect(() => {
    if (mobileMode) return;
    loadServerPrefs(user?.id).then(prefs => {
      if (prefs && prefKey in prefs) {
        setCollapsed(!!prefs[prefKey]);
        localStorage.setItem(CACHE_KEY, JSON.stringify({ ...readCache(), [prefKey]: !!prefs[prefKey] }));
      }
    });
  }, [prefKey, user?.id, mobileMode]);

  const toggle = () => {
    const next = !collapsed;
    setCollapsed(next);
    if (mobileMode) return;
    localStorage.setItem(CACHE_KEY, JSON.stringify({ ...readCache(), [prefKey]: next }));
    prefsPromise = Promise.resolve({ ...readCache() });
    const token = localStorage.getItem('token');
    axios.put(`${API_BASE}/api/merkeskyting/my-preferences`, { ui_collapsed: { [prefKey]: next } },
      { headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
  };

  return (
    <div className={`rounded-lg border text-sm ${VARIANTS[variant]}`} data-testid={`info-tip-${prefKey}`}>
      <button type="button" onClick={toggle} aria-expanded={!collapsed}
        className="w-full flex items-center gap-2 px-3 py-2.5 text-left touch-manipulation"
        data-testid={`info-tip-toggle-${prefKey}`}>
        <Icon className="w-4 h-4 flex-shrink-0" />
        <strong className="flex-1 min-w-0">{title}</strong>
        {collapsed ? <ChevronDown className="w-5 h-5 flex-shrink-0" /> : <ChevronUp className="w-5 h-5 flex-shrink-0" />}
      </button>
      {!collapsed && <div className="px-3 pb-3 pl-9" data-testid={`info-tip-body-${prefKey}`}>{children}</div>}
    </div>
  );
};

export default CollapsibleInfoTip;
